import { useEffect, useState } from "react";

export const useVersionCheck = () => {
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const checkVersion = async () => {
    if (isChecking) return;

    setIsChecking(true);
    try {
      // Fetch version.json with cache-busting timestamp
      const response = await fetch(`/version.json?t=${Date.now()}`);
      const serverVersion = await response.json();

      // Get cached version from localStorage
      const cachedVersion = localStorage.getItem("appVersion");
      const cachedBuildTime = localStorage.getItem("appBuildTime");

      // If no cached version, this is first load - save it
      if (!cachedVersion || !cachedBuildTime) {
        localStorage.setItem("appVersion", serverVersion.version);
        localStorage.setItem("appBuildTime", serverVersion.buildTime);
        setIsChecking(false);
        return;
      }

      // Compare build times - if server version is newer, update is needed
      if (serverVersion.buildTime !== cachedBuildTime) {
        console.log("[Version Check] New version available:", {
          cached: cachedBuildTime,
          server: serverVersion.buildTime,
        });
        setNeedsUpdate(true);
      }
    } catch (error) {
      console.error("[Version Check] Error checking version:", error);
    } finally {
      setIsChecking(false);
    }
  };

  const reloadApp = () => {
    // Clear cache and reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    // Update localStorage with latest version before reload
    fetch(`/version.json?t=${Date.now()}`)
      .then(res => res.json())
      .then(version => {
        localStorage.setItem("appVersion", version.version);
        localStorage.setItem("appBuildTime", version.buildTime);
      })
      .finally(() => {
        // Force reload (equivalent to Ctrl+F5 / Cmd+Shift+R)
        window.location.reload(true);
      });
  };

  useEffect(() => {
    // Check immediately on mount
    checkVersion();

    // Check every 5 minutes
    const interval = setInterval(checkVersion, 5 * 60 * 1000);

    // Check when tab becomes visible (user comes back to the app)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkVersion();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { needsUpdate, reloadApp };
};